import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, MultiSelect, DatePicker, message } from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import {
  apiV2EnergyConsumptionAdminListPost,
  apiV2EnergyConsumptionAdminOrgListPost,
  V2EnergyConsumptionAdminListPostResponse,
  V2EnergyConsumptionAdminOrgListPostResponse,
  V2OuListOwnedPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import qs from 'qs';

type RangeValue = [Dayjs | null, Dayjs | null] | null;
interface ExportModalProps {
  onClose: () => void;
  updateFn: () => void;
  visible: boolean;
}

export type OuInfoProps = Exclude<V2OuListOwnedPostResponse['list'], undefined>[number];
const getWeekRange = (date: dayjs.Dayjs) => {
  const day = date.day();
  const diffToSat = (day + 1) % 7;
  const start = date.startOf('day').subtract(diffToSat, 'day');
  const end = start.add(7, 'day');
  return [start, end];
};
// 计算选择了多少个周期
const calculateWeeks = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): number => {
  const startSaturday = getWeekRange(startDate)[0];
  const endSaturday = getWeekRange(endDate)[0];
  const weeks = endSaturday.diff(startSaturday, 'week');
  return weeks + 1; // 包含起始周期
};

const ExportModal: FC<ExportModalProps> = ({ onClose, updateFn, visible }) => {
  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  const [dates, setDates] = useState<RangeValue>();
  const [MediaOp, setMediaOp] = useState<V2EnergyConsumptionAdminListPostResponse['list']>([]);
  const [sectionOp, setSectionOp] = useState<V2EnergyConsumptionAdminOrgListPostResponse['list']>([]);
  const now = dayjs();
  const [currentPeriodStart, currentPeriodEnd] = getWeekRange(now);

  useEffect(() => {
    apiV2EnergyConsumptionAdminOrgListPost({}).then(res => {
      setSectionOp(res.list);
    });
    apiV2EnergyConsumptionAdminListPost({}).then(res => {
      setMediaOp(res.list);
    });
  }, []);

  const disabledDate = (current: dayjs.Dayjs) => {
    return !current || current < currentPeriodEnd;
  };

  const isSaturdayMidnight = (d: dayjs.Dayjs) => {
    return d.hour() === 0 && d.minute() === 0 && d.second() === 0 && d.day() === 6;
  };

  const adjustRangeToPeriods = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
    const alignedStart = getWeekRange(start)[0];

    let alignedEnd: dayjs.Dayjs;
    if (isSaturdayMidnight(end)) {
      alignedEnd = end;
    } else {
      alignedEnd = getWeekRange(end)[1];
    }

    let periods = alignedEnd.diff(alignedStart, 'day') / 7;

    if (periods < 1) {
      alignedEnd = alignedStart.add(7, 'day');
      periods = 1;
    }

    if (periods > 5) {
      alignedEnd = alignedStart.add(7 * 5, 'day');
      periods = 5;
    }

    return [alignedStart.startOf('day'), alignedEnd.startOf('day')] as [dayjs.Dayjs, dayjs.Dayjs];
  };

  // 弹框确认
  const onOk = () => {
    form.validateFields().then(values => {
      const params = {
        ownerOrgIds: values.ownerOrgIds,
        energyMediumIds: values.energyMediumIds,
        start: values.dates[0].valueOf(),
        end: values.dates[1].valueOf(),
      };
      window.open(`/api/v2/energy/consumption/admin/download?${qs.stringify(params, { arrayFormat: 'repeat' })}`);
    });
  };

  return (
    <Modal title="导出" open={visible} onOk={onOk} contentClassName="modal-form-content" onCancel={onClose}>
      <Form form={form}>
        <Form.Item label="部门" rules={[{ required: true }]} name="ownerOrgIds">
          <MultiSelect
            showSearch
            allowClear
            style={{ width: '100%' }}
            placeholder="请选择"
            optionFilterProp="label"
            options={sectionOp?.map(s => ({ label: s.ownerOrgName, value: s.ownerOrgId }))}
          />
        </Form.Item>

        <Form.Item label="能源介质" rules={[{ required: true }]} name="energyMediumIds">
          <MultiSelect
            showSearch
            allowClear
            style={{ width: '100%' }}
            placeholder="请选择"
            optionFilterProp="label"
            options={MediaOp?.map(i => ({ label: i.energyMediumName, value: i.energyMediumId }))}
          />
        </Form.Item>
        <Form.Item label="日期范围" name="dates" rules={[{ required: true, message: '请选择时间' }]}>
          <RangePicker
            style={{ width: '100%' }}
            value={dates}
            // disabledDate={disabledDate}
            onCalendarChange={val => {
              if (val && val[0] && val[1]) {
                // 检查选择的周期数是否超过5个
                const weeks = calculateWeeks(val[0], val[1]);
                if (weeks > 5) {
                  message.error('最多选择5个周期');
                  return;
                }
                setDates(val);
              }
            }}
            onChange={(val: RangeValue) => {
              if (!val || !val[0] || !val[1]) {
                setDates(val);
                return;
              }
              const [s, e] = val as [dayjs.Dayjs, dayjs.Dayjs];
              const [newStart, newEnd] = adjustRangeToPeriods(s, e);
              setDates([newStart, newEnd]);
              form.setFieldsValue({ dates: [newStart, newEnd] });
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ExportModal;
